import { useSelector } from "react-redux";
import { useMemo } from "react";

import { InternalButton } from "@components/shared/buttons";
import Modal from "@stadium/ui/src/components/ModalNew";
import {
  ModalBody,
  ModalContent,
  ModalDialog,
  ModalHeader,
  ModalFooter,
  CloseButton,
} from "@stadium/ui/src/components/ModalNew/utils";
import Multimedia from "@sharedComponents/multimedia";
import AuthLogin from "@components/auth/buttons/login";

import { useStoreContext } from "@components/store/provider";

import { getPublishStoreRoute } from "@constants/routes";
import { useAuth0V2 } from "@stadium/services/auth0";
import { useCreateShopContext } from "@contexts/create-shop-context";
import useUpdateDummyStore from "@hooks/store/use-update-dummy-store";
import { getMenu } from "@selectors/menu";
import { getAllMenuTaxons } from "@helpers/menu";

import imageIcon from "@stadium/ui/src/assets/images/svg/red-alert-icon.svg";

import styles from "./purge-main.module.scss";

const CreateShopCancelSavedChanges = ({ exitWithoutSaveHandler, closeModal }) => {
  const { isAuthenticated } = useAuth0V2();
  const { countryCode, lang, storeCode, storeNumber, isNewRoute } = useStoreContext();

  const menu = useSelector(getMenu);
  const { allChildren: menuItems } = menu;

  const { allMenuTaxonIds: taxonIds } = useMemo(() => getAllMenuTaxons(menuItems), [menuItems]);

  const { clearDummyStoreParams, dummyStoreNumber } = useCreateShopContext();

  const { loading: isPublishingStore, publishDummyStore } = useUpdateDummyStore(dummyStoreNumber);

  const loginOptions = {
    appState: {
      returnTo: getPublishStoreRoute({
        countryCode,
        lang,
        storeCode,
        storeNumber,
        dummyStoreNumber,
        isNewRoute,
      }),
    },
    signUp: true,
  };

  const handleExit = () => {
    if (exitWithoutSaveHandler) exitWithoutSaveHandler();
    closeModal();
  };

  const handleSaveChanges = () => {
    publishDummyStore({ successCb: clearDummyStoreParams, data: { taxonIds } });
  };

  const renderSubmitCta = () => {
    if (isAuthenticated) {
      return (
        <InternalButton
          onClick={handleSaveChanges}
          isLoading={isPublishingStore}
          className={styles.btnSave}
        >
          Save & Exit
        </InternalButton>
      );
    }

    return (
      <AuthLogin loginOptions={loginOptions}>
        <InternalButton>Log In/Sign Up</InternalButton>
      </AuthLogin>
    );
  };

  return (
    <Modal closeModal={closeModal} className={`modal ${styles.exitModal}`}>
      <ModalDialog className={styles.modalDialog}>
        <ModalContent className={styles.modalContent}>
          <ModalHeader className={styles.modalHeader}>
            <div>
              <h3 className={styles.title}>
                If you leave now, your progress won't be saved.
                <br /> Do you want to save your shop?
              </h3>
              {!isAuthenticated && (
                <p className={styles.info}>
                  Log in or create an account to save your shop and make edits later.
                </p>
              )}
            </div>
            <CloseButton onClick={closeModal} className={styles.closeIcon} />
          </ModalHeader>

          <ModalBody className={styles.modalBody}>
            <div className={styles.imageBlock}>
              <Multimedia type="image" src={imageIcon} height={"96"} width={"96"} />
            </div>
          </ModalBody>

          <ModalFooter className={styles.modalFooter}>
            <InternalButton
              onClick={handleExit}
              styleType="primary-outline"
              className={styles.btnExit}
            >
              Exit&nbsp;<span className={styles.mobileHide}> without saving</span>
            </InternalButton>
            {renderSubmitCta()}
          </ModalFooter>
        </ModalContent>
      </ModalDialog>
    </Modal>
  );
};

export default CreateShopCancelSavedChanges;
